import axios from "axios";
import { Fragment, useState, useEffect } from "react";
import { CustomDateRangePicker } from "components/CustomDateRangePicker";
import { CustomLoading } from "components/CustomLoading";
import { CSVLink } from "react-csv";
import toastr from "toastr";
import { Button, Table } from "react-bootstrap";
import CustomPagination from "components/CustomPagination";
import { EmptyStateTemplate } from "./Empty_state_Template";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSortDown, faSortUp, faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import "../../styles/Reports.css";
import base64 from "../../data/base64.json";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import moment from "moment";
pdfMake.vfs = pdfFonts.pdfMake.vfs;

interface BreakdownRow {
  type: string;
  description: string;
  amount: number;
  claimNo: string | null;
  approvedAmount: number | null;
  adminFee: number | null;
  taxes: number | null;
  submittedDate: string | null;
  approvedDate: string | null;
}

interface Withdrawal {
  entryId: string;
  date: string;
  fileNum: number;
  amount: number;
  status: string;
  currentFunds: number;
  floatAmount: number;
  approvedClaims: number;
  breakdown: BreakdownRow[];
}

const STATUS_LABELS: { [key: string]: { label: string; color: string; bg: string } } = {
  pending: { label: "Pending", color: "#b9770f", bg: "#fdf3e2" },
  complete: { label: "Completed", color: "#2f7a5b", bg: "#e8f5ee" },
  failed: { label: "Failed", color: "#c0392b", bg: "#fdecea" },
};

const money = (n: number | null | undefined) =>
  n === null || n === undefined
    ? ""
    : "$" + Number(n).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PadWithdrawalsTable = ({ policyId }) => {
  const date = new Date();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<Withdrawal[]>([]);
  const [dataToExport, setDataToExport] = useState<any[]>([]);
  const [fromDate, setFromDate] = useState(new Date(date.getFullYear(), date.getMonth() - 2, 1));
  const [toDate, setToDate] = useState(new Date());
  const [tempFromDate, setTempFromDate] = useState(new Date(date.getFullYear(), date.getMonth() - 2, 1));
  const [tempToDate, setTempToDate] = useState(new Date());
  const [sortOrder, setSortOrder] = useState("desc");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [debitFailed, setDebitFailed] = useState(false);

  useEffect(() => {
    fetchData();
    axios
      .get(`/api/pad/status?policyId=${policyId}`)
      .then((res) => {
        if (res.data.success) {
          setDebitFailed(res.data.state === "suspended_failed");
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async (from = fromDate, to = toDate) => {
    try {
      const res = await axios.get(
        `/api/pad/withdrawals?policyId=${policyId}&fromDate=${from.toISOString()}&toDate=${to.toISOString()}`
      );
      if (res.data.success) {
        const withdrawals: Withdrawal[] = res.data.withdrawals || [];
        setData(withdrawals);
        setDataToExport(buildExport(withdrawals, from, to));
      } else {
        setData([]);
        setDataToExport([]);
      }
    } catch (error) {
      toastr.error("Error");
    } finally {
      setLoading(false);
      setExpanded(null);
      setCurrentPage(1);
      setSortOrder("desc");
    }
  };

  const buildExport = (withdrawals: Withdrawal[], from: Date, to: Date) => {
    const rows: any[] = [
      ["Date Range", `${from.toISOString().slice(0, 10)} - ${to.toISOString().slice(0, 10)}`],
      ["Date", "File No.", "Amount", "Status"],
    ];
    withdrawals.forEach((w) => {
      rows.push([
        moment(w.date).format("YYYY-MM-DD"),
        w.fileNum,
        Number(w.amount).toFixed(2),
        STATUS_LABELS[w.status]?.label || w.status,
      ]);
    });
    return rows;
  };

  const sortByDate = () => {
    setData([...data].reverse());
    const header = dataToExport.slice(0, 2);
    const body = dataToExport.slice(2).reverse();
    setDataToExport([...header, ...body]);
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const exportToPdf = () => {
    const header = dataToExport[1].map((cell) => ({ text: String(cell), style: "tableHeader", alignment: "left" }));
    const body = dataToExport.slice(2).map((row) =>
      row.map((cell, i) => ({ text: String(cell ?? ""), style: "tableBody", alignment: i === 2 ? "right" : "left" }))
    );

    const docDefinition: any = {
      pageSize: "A4",
      content: [
        { columns: [{ image: base64.logo, width: 200 }, { text: `Date Range: ${dataToExport[0][1]}`, fontSize: 12, bold: true, alignment: "right", margin: [0, 0, 0, 8] }] },
        { text: "Pre-Authorized Debit (PAD) Withdrawals", fontSize: 16, bold: true, alignment: "center", margin: [10, 40, 0, 10] },
        { style: "table", table: { widths: ["auto", "auto", "auto", "auto"], body: [header, ...body] } },
      ],
      styles: {
        table: { margin: [70, 5, 50, 15], alignment: "center" },
        tableHeader: { bold: true, fontSize: 12, color: "black" },
        tableBody: { fontSize: 10, color: "black" },
      },
    };

    pdfMake.createPdf(docDefinition).download("PAD_Withdrawals_Report.pdf");
  };

  const handleSubmit = async () => {
    setFromDate(tempFromDate);
    setToDate(tempToDate);
    setLoading(true);
    await fetchData(tempFromDate, tempToDate);
  };

  const endOfToday = (() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  })();

  const indexOfLastRecord = currentPage * itemsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - itemsPerPage;
  const currentRecords = data.slice(indexOfFirstRecord, indexOfLastRecord);
  const nPages = Math.ceil(data.length / itemsPerPage);

  const statusPill = (status: string) => {
    const s = STATUS_LABELS[status] || { label: status, color: "#46566a", bg: "#eef1f4" };
    return (
      <span style={{ background: s.bg, color: s.color, borderRadius: 999, padding: "3px 10px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
        {s.label}
      </span>
    );
  };

  const breakdownLabel = (b: BreakdownRow) => {
    if (b.type === "claim") {
      return "Claim " + (b.claimNo || "");
    }
    return b.description || (b.type === "topup" ? "Float top-up" : "Adjustment");
  };

  const renderBreakdown = (w: Withdrawal) => (
    <tr key={w.entryId + "-detail"}>
      <td colSpan={5} style={{ background: "#f7f9fb", padding: "14px 18px" }}>
        <div style={{ fontSize: 12, color: "#6b7886", marginBottom: 10 }}>
          At the time this file was built: account funds {money(w.currentFunds)} · float {money(w.floatAmount)} · approved claims {money(w.approvedClaims)}
        </div>
        <Table size="sm" style={{ marginBottom: 0, background: "#fff" }}>
          <thead>
            <tr>
              <th className="table-text-content">Item</th>
              <th className="table-text-content">Submitted</th>
              <th className="table-text-content">Approved</th>
              <th style={{ textAlign: "right" }}>Approved Amt</th>
              <th style={{ textAlign: "right" }}>Admin Fee</th>
              <th style={{ textAlign: "right" }}>Taxes</th>
              <th style={{ textAlign: "right" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {w.breakdown.map((b, i) => (
              <tr key={i}>
                <td className="table-text-content">{breakdownLabel(b)}</td>
                <td className="table-text-content">{b.submittedDate ? moment(b.submittedDate).format("YYYY-MM-DD") : ""}</td>
                <td className="table-text-content">{b.approvedDate ? moment(b.approvedDate).format("YYYY-MM-DD") : ""}</td>
                <td style={{ textAlign: "right" }}>{money(b.approvedAmount)}</td>
                <td style={{ textAlign: "right" }}>{money(b.adminFee)}</td>
                <td style={{ textAlign: "right" }}>{money(b.taxes)}</td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>{money(b.amount)}</td>
              </tr>
            ))}
            {w.breakdown.length === 0 && (
              <tr>
                <td colSpan={7} className="table-text-content" style={{ color: "#6b7886" }}>
                  No breakdown recorded for this withdrawal.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </td>
    </tr>
  );

  return (
    <div className="pad-withdrawals-table report-table-container">
      {loading ? (
        <CustomLoading />
      ) : (
        <>
          {debitFailed && (
            <div style={{ background: "#fdecea", border: "1px solid #f5c6c0", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#7f1d1d" }}>
              <strong style={{ color: "#c0392b" }}>Your last Pre-Authorized Debit was unsuccessful.</strong>{" "}
              Automatic debits are paused. Contact{" "}
              <a href="mailto:support@nationalhealthclaim.com">support@nationalhealthclaim.com</a> for details.
            </div>
          )}
          <div className="report-toolbar">
            <div className="toolbar-left-section">
              <div className="d-flex align-items-center justify-content-start" style={{ gap: 20 }}>
                <CustomDateRangePicker
                  fromDate={tempFromDate}
                  toDate={tempToDate}
                  maxDate={endOfToday}
                  setFromDate={setTempFromDate}
                  setToDate={setTempToDate}
                />
                <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                  {loading ? "Loading..." : "Submit"}
                </button>
              </div>
            </div>
            <div className="toolbar-right-section">
              <div className="export-button-container">
                <Button variant="primary" className="export-to-pdf" onClick={exportToPdf} disabled={data.length === 0}>
                  <span className="pdf-text">Export to PDF</span>
                </Button>
                <CSVLink
                  data={dataToExport}
                  filename={"PAD_Withdrawals_Report.csv"}
                  className="export-button"
                  onClick={(event) => { if (data.length === 0) event.preventDefault(); }}
                >
                  <Button variant="primary" className="csv-button export-to-csv" disabled={data.length === 0}>
                    <span className="csv-text">Export to CSV</span>
                  </Button>
                </CSVLink>
              </div>
            </div>
          </div>
          {data.length > 0 ? (
            <>
              <div style={{ fontSize: 13, color: "#6b7886", margin: "0 0 10px" }}>
                Each row is one pre-authorized withdrawal from your bank account. Click a row to see exactly what made up the amount: float top-up, claims awaiting reimbursement, and any adjustment.
              </div>
              <Table hover className="report-table">
                <thead>
                  <tr>
                    <th style={{ width: 30 }} />
                    <th className="table-text-content" onClick={sortByDate} style={{ cursor: "pointer" }}>
                      Date
                      <FontAwesomeIcon
                        icon={sortOrder === "asc" ? faSortUp : faSortDown}
                        className={sortOrder === "asc" ? "asc-icon" : "desc-icon"}
                      />
                    </th>
                    <th className="table-text-content">File No.</th>
                    <th style={{ textAlign: "right" }}>Amount</th>
                    <th className="table-text-content">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRecords.map((w) => (
                    <Fragment key={w.entryId}>
                      <tr
                        className="table-body-item"
                        style={{ cursor: "pointer" }}
                        onClick={() => setExpanded(expanded === w.entryId ? null : w.entryId)}
                      >
                        <td>
                          <FontAwesomeIcon icon={expanded === w.entryId ? faChevronDown : faChevronRight} style={{ color: "#8593a3" }} />
                        </td>
                        <td className="table-text-content">{moment(w.date).format("YYYY-MM-DD")}</td>
                        <td className="table-text-content">{w.fileNum}</td>
                        <td style={{ textAlign: "right", fontWeight: 600 }}>{money(w.amount)}</td>
                        <td className="table-text-content">{statusPill(w.status)}</td>
                      </tr>
                      {expanded === w.entryId && renderBreakdown(w)}
                    </Fragment>
                  ))}
                </tbody>
              </Table>
              <div className="pagination-container">
                <div className="pagination-info">
                  <span>
                    Showing {indexOfFirstRecord + 1} - {indexOfFirstRecord + currentRecords.length} entries out of {data.length} entries
                  </span>
                </div>
                <CustomPagination nPages={nPages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
              </div>
            </>
          ) : (
            <EmptyStateTemplate />
          )}
        </>
      )}
    </div>
  );
};

export default PadWithdrawalsTable;
